import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Alert from "@mui/material/Alert";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";

import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";

import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";

import {
  getRequestDisplayLocations,
  stripRouteBlockFromDescription,
} from "./resourceRequestLocations";
import { getResourceRequestQuestions } from "./Users/components/forms/resourceRequestQuestionBank";

import {
  getMissionDocumentSignedUrl,
  parseMissionImageRef,
} from "utils/missionDocuments";

import curved0 from "assets/images/curved-images/curved0.jpg";

import { useAuth } from "context/AuthContext";
import {
  getResourceRequestById,
  getActions,
  addResourceRequestAction,
} from "./Users/services/graphqlService";
import { canEditRequestForProfile } from "./requestOwnerUtils";

const capitalize = (value = "") =>
  value.charAt(0).toUpperCase() + value.slice(1);

const prettify = (value) =>
  value
    ? value
        .toString()
        .replace(/[_-]/g, " ")
        .split(" ")
        .map((chunk) => capitalize(chunk))
        .join(" ")
    : "—";

/**
 * Formats a stored due-date value for display. Legacy date-only values
 * (e.g. "2026-09-15", from before this field captured a time) render as a
 * plain date, parsed as a local calendar day to avoid a UTC off-by-one.
 * Values that include a time (the current "datetime-local" format) render
 * with both date and time, matching MissionDetails.js's date/time display.
 */
const formatDate = (value) => {
  if (!value) return "—";
  const raw = String(value).trim();
  if (!raw) return "—";
  const dateOnly = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnly) {
    const parsed = new Date(
      Number(dateOnly[1]),
      Number(dateOnly[2]) - 1,
      Number(dateOnly[3]),
    );
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

function ResourceRequestOverviewHeader({ request, idToken }) {
  const dueDateLabel = formatDate(request.dueDate);
  const isAsap =
    request.isAsap === true ||
    (typeof request.isAsap === "string" &&
      request.isAsap.toLowerCase() === "yes");

  const imageRef = useMemo(
    () => parseMissionImageRef(request?.image),
    [request?.image],
  );
  const [headerImageUrl, setHeaderImageUrl] = useState(null);

  useEffect(() => {
    if (!imageRef?.fileName || !idToken) {
      setHeaderImageUrl(null);
      return undefined;
    }

    let cancelled = false;
    getMissionDocumentSignedUrl(
      {
        fileName: imageRef.fileName,
        bucket: imageRef.bucket || "info.avipair.com",
        key: imageRef.fileName,
      },
      idToken,
    )
      .then((url) => {
        if (!cancelled) setHeaderImageUrl(url);
      })
      .catch((err) => {
        console.error("Failed to load flight request image:", err);
        if (!cancelled) setHeaderImageUrl(null);
      });

    return () => {
      cancelled = true;
    };
  }, [imageRef?.fileName, imageRef?.bucket, idToken]);

  const heroImage = headerImageUrl || curved0;

  const subtitleParts =
    [
      request.type && prettify(request.type),
      dueDateLabel !== "—" && `Due ${dueDateLabel}`,
      isAsap && "Urgent",
    ]
      .filter(Boolean)
      .join(" • ") || "Flight Request";

  const descriptionPlain = stripRouteBlockFromDescription(request.description);

  return (
    <SoftBox position="relative">
      <SoftBox
        display="flex"
        alignItems="center"
        position="relative"
        minHeight="18.75rem"
        borderRadius="xl"
        sx={{
          backgroundImage: ({
            functions: { rgba, linearGradient },
            palette: { gradients },
          }) =>
            `${linearGradient(
              rgba(gradients.info.main, 0.6),
              rgba(gradients.info.state, 0.6),
            )}, url(${heroImage})`,
          backgroundSize: "cover",
          backgroundPosition: "50%",
          overflow: "hidden",
        }}
      />
      <Card
        sx={{
          backdropFilter: "saturate(200%) blur(30px)",
          backgroundColor: ({ functions: { rgba }, palette: { white } }) =>
            rgba(white.main, 0.8),
          boxShadow: ({ boxShadows: { navbarBoxShadow } }) => navbarBoxShadow,
          position: "relative",
          mt: -8,
          mx: 3,
          py: 2,
          px: 2,
        }}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12}>
            <SoftBox lineHeight={1}>
              <SoftTypography variant="h5" fontWeight="medium">
                {request.title || "Untitled Flight Request"}
              </SoftTypography>
              <SoftTypography variant="button" color="text" fontWeight="medium">
                {subtitleParts}
              </SoftTypography>
              {descriptionPlain && (
                <SoftTypography
                  variant="body2"
                  color="text"
                  mt={1}
                  maxWidth={720}
                >
                  {descriptionPlain}
                </SoftTypography>
              )}
            </SoftBox>
          </Grid>
        </Grid>
      </Card>
    </SoftBox>
  );
}

function FlightRequestImageSection({ image, idToken }) {
  const imageRef = useMemo(() => parseMissionImageRef(image), [image]);
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!imageRef?.fileName || !idToken) {
      setImageUrl(null);
      setError(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    getMissionDocumentSignedUrl(
      {
        fileName: imageRef.fileName,
        bucket: imageRef.bucket || "info.avipair.com",
        key: imageRef.fileName,
      },
      idToken,
    )
      .then((url) => {
        if (!cancelled) {
          setImageUrl(url);
          setError(null);
        }
      })
      .catch((err) => {
        console.error("Failed to load flight request image:", err);
        if (!cancelled) {
          setImageUrl(null);
          setError(err.message || "Failed to load image");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [imageRef?.fileName, imageRef?.bucket, idToken]);

  if (!imageRef?.fileName) return null;

  return (
    <>
      <Divider />
      <SoftBox pt={2} px={2} pb={2}>
        <SoftTypography variant="h5" fontWeight="bold" mb={2}>
          Image
        </SoftTypography>
        {loading ? (
          <SoftBox display="flex" alignItems="center" gap={1}>
            <CircularProgress size={22} />
            <SoftTypography variant="body2" color="text">
              Loading image…
            </SoftTypography>
          </SoftBox>
        ) : error ? (
          <Alert severity="warning">{error}</Alert>
        ) : imageUrl ? (
          <SoftBox
            component="img"
            src={imageUrl}
            alt="Flight request"
            sx={{
              maxWidth: "100%",
              maxHeight: 420,
              borderRadius: "0.75rem",
              objectFit: "contain",
              display: "block",
              bgcolor: "grey.100",
            }}
          />
        ) : (
          <SoftTypography variant="body2" color="text">
            No image available.
          </SoftTypography>
        )}
      </SoftBox>
    </>
  );
}

function ResourceRequestDetailsPage() {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const { user, profile, tokenRole, tokenEntityType, isGuest } = useAuth();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actions, setActions] = useState([]);
  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const [actionSubmitting, setActionSubmitting] = useState(false);
  const canEditRequest =
    request &&
    canEditRequestForProfile(request, {
      profileId: profile?.profileId,
      tokenRole,
      tokenEntityType,
      isGuest,
    });

  useEffect(() => {
    if (!requestId || !user?.id_token) {
      setRequest(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);

    const loadRequest = async () => {
      // The save mutation may return before the new record is readable by the
      // query path. Retry "not found" responses briefly to cover that window.
      const retryDelays = [0, 350, 700, 1400, 2500];

      for (let attempt = 0; attempt < retryDelays.length; attempt += 1) {
        if (retryDelays[attempt] > 0) {
          await new Promise((resolve) =>
            window.setTimeout(resolve, retryDelays[attempt]),
          );
        }
        if (cancelled) return;

        try {
          const res = await getResourceRequestById(
            requestId,
            user.id_token,
          );
          const data = res?.data;
          if (!data) {
            if (attempt < retryDelays.length - 1) continue;
            setRequest(null);
            return;
          }

          let parsed = typeof data === "string" ? JSON.parse(data) : data;
          if (parsed && !parsed.requestId && !parsed.title) {
            const list =
              parsed.ResourceRequests ||
              parsed.ResourceRequestsList ||
              parsed.AirportsList ||
              parsed.items ||
              (Array.isArray(parsed) ? parsed : null);
            if (Array.isArray(list) && list.length > 0) {
              parsed = list[0];
            }
          }
          if (cancelled) return;
          setRequest(parsed || null);
          return;
        } catch (error) {
          const isNotFound = /not found/i.test(String(error?.message || ""));
          if (isNotFound && attempt < retryDelays.length - 1) continue;
          if (!cancelled) setRequest(null);
          return;
        }
      }
    };

    loadRequest().finally(() => {
      if (!cancelled) setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [requestId, user?.id_token]);

  useEffect(() => {
    if (!user?.id_token) return;
    let cancelled = false;
    getActions("ResourceRequest", user.id_token)
      .then((list) => {
        if (!cancelled) setActions(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setActions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id_token]);

  if (loading) {
    return (
      <SoftBox
        py={3}
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight={220}
      >
        <CircularProgress />
      </SoftBox>
    );
  }

  if (!request) {
    return (
      <SoftBox py={3}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Flight request not found.
        </Alert>
        <SoftButton
          variant="text"
          color="dark"
          onClick={() => navigate("/dashboard/requests")}
          startIcon={<Icon>arrow_back</Icon>}
        >
          Back to Flight Requests
        </SoftButton>
      </SoftBox>
    );
  }

  const isAsap =
    request.isAsap === true ||
    (typeof request.isAsap === "string" &&
      request.isAsap.toLowerCase() === "yes");
  const dueDateLabel = formatDate(request.dueDate);

  const routeLoc = getRequestDisplayLocations(request);
  const originDisplay =
    routeLoc.locationCity || routeLoc.locationState
      ? `${prettify(routeLoc.locationCity || "—")}, ${prettify(
          routeLoc.locationState || "—",
        )}`
      : "—";
  const destinationDisplay =
    routeLoc.arrivalCity || routeLoc.arrivalState
      ? `${prettify(routeLoc.arrivalCity || "—")}, ${prettify(
          routeLoc.arrivalState || "—",
        )}`
      : "—";

  const passengerList = request.passengerItem || request.PassengerItem || [];
  const showCargoSection =
    request.includesCargo === "Yes" || (request.cargoItems?.length > 0);
  const showPassengerSection =
    request.includesPassengers === "Yes" || passengerList.length > 0;

  return (
    <SoftBox py={3}>
      <SoftBox
        mb={3}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={2}
        flexWrap="wrap"
      >
        <SoftButton
          variant="text"
          color="dark"
          onClick={() => navigate("/dashboard/requests")}
          startIcon={<Icon>arrow_back</Icon>}
        >
          Back to Flight Requests
        </SoftButton>

        <SoftBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <SoftButton
            variant="gradient"
            color="info"
            startIcon={<Icon>bolt</Icon>}
            onClick={(e) => setActionMenuAnchorEl(e.currentTarget)}
          >
            Action
          </SoftButton>
          <Menu
            anchorEl={actionMenuAnchorEl}
            open={!!actionMenuAnchorEl}
            onClose={() => setActionMenuAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            PaperProps={{
              sx: { minWidth: 180 },
            }}
          >
            {actions.map((action, idx) => (
              <MenuItem
                key={action.actionId ?? action.type ?? idx}
                onClick={() => {
                  setActionMenuAnchorEl(null);
                  setPendingAction(action);
                }}
              >
                {action.label ?? action.message ?? action.type}
              </MenuItem>
            ))}
            {actions.length === 0 && (
              <MenuItem disabled>No actions available</MenuItem>
            )}
          </Menu>
          <Dialog
            open={!!pendingAction}
            onClose={() => !actionSubmitting && setPendingAction(null)}
            maxWidth="xs"
            fullWidth
          >
            <DialogTitle>Confirm action</DialogTitle>
            <DialogContent>
              <DialogContentText>
                {pendingAction
                  ? `Are you sure you want to "${
                      pendingAction.message ??
                      pendingAction.label ??
                      pendingAction.type
                    }"?`
                  : ""}
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <SoftButton
                variant="text"
                color="secondary"
                onClick={() => setPendingAction(null)}
                disabled={actionSubmitting}
              >
                Cancel
              </SoftButton>
              <SoftButton
                variant="gradient"
                color="info"
                onClick={async () => {
                  if (!pendingAction || !user?.id_token || !request) return;
                  const idToUse = request.requestId ?? request.id ?? requestId;
                  if (!idToUse) return;
                  setActionSubmitting(true);
                  try {
                    const actionMessage =
                      pendingAction.message ??
                      pendingAction.label ??
                      pendingAction.type;
                    await addResourceRequestAction(
                      idToUse,
                      {
                        message: actionMessage,
                        type: pendingAction.type,
                        subType: pendingAction.subType ?? undefined,
                      },
                      user.id_token,
                    );
                    setPendingAction(null);
                    window.dispatchEvent(
                      new CustomEvent("globalNotification", {
                        detail: {
                          type: "success",
                          title: "Action submitted",
                          message:
                            actionMessage ?? "Action completed successfully.",
                        },
                      }),
                    );
                  } catch (err) {
                    window.dispatchEvent(
                      new CustomEvent("globalNotification", {
                        detail: {
                          type: "error",
                          title: "Action failed",
                          message: err?.message ?? "Failed to submit action.",
                        },
                      }),
                    );
                  } finally {
                    setActionSubmitting(false);
                  }
                }}
                disabled={actionSubmitting}
              >
                {actionSubmitting ? (
                  <>
                    <CircularProgress
                      size={20}
                      sx={{ mr: 1 }}
                      color="inherit"
                    />
                    Submitting…
                  </>
                ) : (
                  "OK"
                )}
              </SoftButton>
            </DialogActions>
          </Dialog>
          {canEditRequest && (
            <SoftButton
              variant="outlined"
              color="info"
              startIcon={<Icon>edit</Icon>}
              onClick={() =>
                navigate("/dashboard/requests", {
                  state: {
                    editRequestId: request.requestId || request.id || requestId,
                  },
                })
              }
            >
              Edit
            </SoftButton>
          )}
        </SoftBox>
      </SoftBox>

      <ResourceRequestOverviewHeader
        request={request}
        idToken={user?.id_token}
      />

      <SoftBox mt={5}>
        {/* Request Overview - MiniStatisticsCard row */}
        <SoftBox pt={2} px={2} pb={2}>
          <SoftTypography variant="h5" fontWeight="bold" mb={2}>
            Request Overview
          </SoftTypography>
          <Grid container spacing={3} alignItems="stretch">
            <Grid item xs={12} sm={6} md={4} sx={{ display: "flex" }}>
              <SoftBox
                sx={{
                  flex: 1,
                  minWidth: 0,
                  "& .MuiCard-root": { height: "100%" },
                }}
              >
                <MiniStatisticsCard
                  title={{ fontWeight: "medium", text: "Requester" }}
                  count={request.requesterName || "—"}
                  icon={{ color: "info", component: "person" }}
                  direction="left"
                />
              </SoftBox>
            </Grid>
            <Grid item xs={12} sm={6} md={4} sx={{ display: "flex" }}>
              <SoftBox
                sx={{
                  flex: 1,
                  minWidth: 0,
                  "& .MuiCard-root": { height: "100%" },
                }}
              >
                <MiniStatisticsCard
                  title={{ fontWeight: "medium", text: "Due Date" }}
                  count={dueDateLabel}
                  icon={{ color: "success", component: "event" }}
                  direction="left"
                />
              </SoftBox>
            </Grid>
            <Grid item xs={12} sm={6} md={4} sx={{ display: "flex" }}>
              <SoftBox
                sx={{
                  flex: 1,
                  minWidth: 0,
                  "& .MuiCard-root": { height: "100%" },
                }}
              >
                <MiniStatisticsCard
                  title={{ fontWeight: "medium", text: "Is Urgent" }}
                  count={isAsap ? "Yes" : "No"}
                  icon={{
                    color: isAsap ? "error" : "secondary",
                    component: "bolt",
                  }}
                  direction="left"
                />
              </SoftBox>
            </Grid>
          </Grid>
        </SoftBox>

        <Divider />

        {/* Origin & Destination */}
        <SoftBox pt={2} px={2} pb={2}>
          <SoftTypography variant="h5" fontWeight="bold" mb={2}>
            Origin & Destination
          </SoftTypography>
          <SoftBox
            display="flex"
            alignItems="center"
            flexWrap="wrap"
            gap={3}
            p={3}
            sx={(theme) => ({
              borderRadius: "1rem",
              backgroundColor:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.05)"
                  : "rgba(99, 110, 255, 0.08)",
              border: `1px solid ${theme.palette.divider}`,
              width: "100%",
            })}
          >
            <SoftBox
              display="flex"
              alignItems="center"
              gap={1.5}
              flex="1"
              minWidth={140}
            >
              <SoftBox
                display="flex"
                alignItems="center"
                justifyContent="center"
                width={48}
                height={48}
                borderRadius="md"
                sx={(theme) => ({
                  backgroundColor: theme.palette.success.main,
                  color: theme.palette.common.white,
                })}
              >
                <Icon sx={{ fontSize: "1.75rem" }}>place</Icon>
              </SoftBox>
              <SoftBox>
                <SoftTypography
                  variant="caption"
                  color="text"
                  fontWeight="bold"
                >
                  Origin
                </SoftTypography>
                <SoftTypography variant="h6" fontWeight="bold" display="block">
                  {originDisplay}
                </SoftTypography>
              </SoftBox>
            </SoftBox>
            <Icon color="info" sx={{ fontSize: "2rem" }}>
              arrow_forward
            </Icon>
            <SoftBox
              display="flex"
              alignItems="center"
              gap={1.5}
              flex="1"
              minWidth={140}
            >
              <SoftBox
                display="flex"
                alignItems="center"
                justifyContent="center"
                width={48}
                height={48}
                borderRadius="md"
                sx={(theme) => ({
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.common.white,
                })}
              >
                <Icon sx={{ fontSize: "1.75rem" }}>flight_land</Icon>
              </SoftBox>
              <SoftBox>
                <SoftTypography
                  variant="caption"
                  color="text"
                  fontWeight="bold"
                >
                  Destination
                </SoftTypography>
                <SoftTypography variant="h6" fontWeight="bold" display="block">
                  {destinationDisplay}
                </SoftTypography>
              </SoftBox>
            </SoftBox>
          </SoftBox>
        </SoftBox>

        <FlightRequestImageSection
          image={request.image}
          idToken={user?.id_token}
        />

        {request.additionalNotes && (
          <>
            <Divider />
            <SoftBox pt={2} px={2} pb={2}>
              <SoftTypography variant="h5" fontWeight="bold" mb={1}>
                Additional Notes
              </SoftTypography>
              <SoftTypography
                variant="body2"
                color="text"
                sx={{ whiteSpace: "pre-wrap" }}
              >
                {request.additionalNotes}
              </SoftTypography>
            </SoftBox>
          </>
        )}

        {showCargoSection && (
          <>
            <Divider />
            <SoftBox pt={2} px={2} pb={2}>
              <SoftTypography variant="h5" fontWeight="bold" mb={2}>
                Cargo Items
              </SoftTypography>
              {request.cargoItems?.length > 0 ? (
                <TableContainer
                  sx={(theme) => ({
                    borderRadius: "1rem",
                    border: `1px solid ${theme.palette.divider}`,
                    overflow: "auto",
                    width: "100%",
                  })}
                >
                  <Table
                    size="small"
                    component="table"
                    sx={{
                      width: "100%",
                      minWidth: 300,
                      tableLayout: "fixed",
                      "& thead": {
                        width: "100%",
                        display: "table-header-group",
                      },
                      "& thead tr": { width: "100%", display: "table-row" },
                    }}
                  >
                    <colgroup>
                      <col style={{ width: "55%" }} />
                      <col style={{ width: "15%" }} />
                      <col style={{ width: "30%" }} />
                    </colgroup>
                    <TableHead>
                      <TableRow
                        sx={(theme) => ({
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.06)"
                              : "rgba(99, 110, 255, 0.08)",
                        })}
                      >
                        <TableCell>Description</TableCell>
                        <TableCell align="right">Qty</TableCell>
                        <TableCell align="right">Weight (lbs)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {request.cargoItems.map((row, i) => (
                        <TableRow key={i} hover>
                          <TableCell>{row.description}</TableCell>
                          <TableCell align="right">{row.quantity ?? "—"}</TableCell>
                          <TableCell align="right">{row.weightLbs}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : request.initialCargo ? (
                <SoftTypography variant="body2" color="text" fontStyle="italic">
                  {request.initialCargo}
                </SoftTypography>
              ) : (
                <SoftTypography variant="body2" color="text" fontStyle="italic">
                  No cargo items
                </SoftTypography>
              )}
            </SoftBox>
          </>
        )}

        {showPassengerSection && (
          <>
            <Divider />
            <SoftBox pt={2} px={2} pb={2}>
              <SoftTypography variant="h5" fontWeight="bold" mb={2}>
                Passengers
              </SoftTypography>
              {passengerList.length > 0 ? (
                <SoftBox display="flex" flexDirection="column" gap={2}>
                  {passengerList.map((row, i) => {
                    const isAnimal =
                      row.isAnimal === true ||
                      String(row.isAnimal || "")
                        .trim()
                        .toLowerCase() === "yes";
                    const detailQuestions = getResourceRequestQuestions(
                      request.type,
                      { isAnimal },
                    )
                      .map((item) => ({
                        ...item,
                        response: row[item.id],
                      }))
                      .filter(
                        (item) =>
                          item.response != null &&
                          String(item.response).trim() !== "",
                      );

                    return (
                      <SoftBox
                        key={i}
                        p={2}
                        sx={(theme) => ({
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: "0.75rem",
                        })}
                      >
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={3}>
                            <SoftTypography
                              variant="caption"
                              color="text"
                              fontWeight="bold"
                              display="block"
                            >
                              Name
                            </SoftTypography>
                            <SoftTypography variant="body2">
                              {row.fullName || "—"}
                            </SoftTypography>
                          </Grid>
                          <Grid item xs={6} md={2}>
                            <SoftTypography
                              variant="caption"
                              color="text"
                              fontWeight="bold"
                              display="block"
                            >
                              Type
                            </SoftTypography>
                            <SoftTypography variant="body2">
                              {isAnimal ? "Animal" : "Human"}
                            </SoftTypography>
                          </Grid>
                          <Grid item xs={6} md={2}>
                            <SoftTypography
                              variant="caption"
                              color="text"
                              fontWeight="bold"
                              display="block"
                            >
                              Age
                            </SoftTypography>
                            <SoftTypography variant="body2">
                              {row.age ?? "—"}
                            </SoftTypography>
                          </Grid>
                          <Grid item xs={6} md={2}>
                            <SoftTypography
                              variant="caption"
                              color="text"
                              fontWeight="bold"
                              display="block"
                            >
                              Weight (lbs)
                            </SoftTypography>
                            <SoftTypography variant="body2">
                              {row.weightLbs ?? "—"}
                            </SoftTypography>
                          </Grid>
                          <Grid item xs={12} md={3}>
                            <SoftTypography
                              variant="caption"
                              color="text"
                              fontWeight="bold"
                              display="block"
                            >
                              Special Needs
                            </SoftTypography>
                            <SoftTypography variant="body2">
                              {row.specialNeeds || "—"}
                            </SoftTypography>
                          </Grid>
                          {detailQuestions.map((item) => (
                            <Grid item xs={12} md={6} key={item.id}>
                              <SoftTypography
                                variant="caption"
                                color="text"
                                fontWeight="bold"
                                display="block"
                              >
                                {item.label}
                              </SoftTypography>
                              <SoftTypography
                                variant="body2"
                                sx={{ whiteSpace: "pre-wrap" }}
                              >
                                {String(item.response)}
                              </SoftTypography>
                            </Grid>
                          ))}
                        </Grid>
                      </SoftBox>
                    );
                  })}
                </SoftBox>
              ) : request.initialPassengers ? (
                <SoftTypography variant="body2" color="text" fontStyle="italic">
                  {request.initialPassengers}
                </SoftTypography>
              ) : (
                <SoftTypography variant="body2" color="text" fontStyle="italic">
                  No passengers
                </SoftTypography>
              )}
            </SoftBox>
          </>
        )}
      </SoftBox>
    </SoftBox>
  );
}

export default ResourceRequestDetailsPage;
